import { usePrefetchQuery, useQuery } from '@tanstack/react-query'
import { api } from '../base'

import { Bank } from '@synapex/prisma/client'
import {
    WorkspaceDescriptor,
    WorkspaceWithLinkDescriptor,
} from '@synapex/shared2/entities'
import {
    LinkedLinksResponse,
    VkOrdHistoryResponse,
    KktuResponse,
    InnInfo,
} from '@synapex/shared2/gateway-rest-api'
import { AsyncReturnType } from '@synapex/tools/typescript'
import { QueryClient, UseQueryOptions } from '@tanstack/react-query'
import { getBankByBic, getBanks, getVkOrdHistory } from './ord.service'

import { apiQueryKey } from '../query-key'

export const { useKktuPrefetchQuery, useKktuQuery } = (() => {
    const core = {
        queryKey: ['kktu'] as const,
        queryFn: () => api.get<KktuResponse>('/ord/kktu'),
        staleTime: 1000 * 60 * 60 * 24,
    }

    return {
        useKktuPrefetchQuery: () => usePrefetchQuery<KktuResponse, Error>(core),
        useKktuQuery: () =>
            useQuery<KktuResponse, Error>({
                ...core,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
            }),
    }
})()

export function useGetBanks(
    search?: string,
    options?: Omit<UseQueryOptions<Bank[], Error>, 'queryKey'>
) {
    return useQuery<Bank[], Error>({
        queryKey: ['banks', 'search', search],
        queryFn: () => getBanks(search),
        staleTime: 1000 * 60 * 60 * 24,
        ...options,
    })
}

const getBankByBicOptions = (bic: string) => ({
    queryKey: ['banks', bic] as const,
    queryFn: () => getBankByBic(bic),
    staleTime: 1000 * 60 * 60 * 24,
})

export function useGetBankByBic(
    bic: string,
    options?: Omit<
        UseQueryOptions<AsyncReturnType<typeof getBankByBic>, Error>,
        'queryKey'
    >
) {
    return useQuery<AsyncReturnType<typeof getBankByBic>, Error>({
        ...getBankByBicOptions(bic),
        ...options,
    })
}

export const queryClientGetBankByBic = (
    queryClient: QueryClient,
    bic: string
) => queryClient.fetchQuery(getBankByBicOptions(bic))

export const queryClientCheckInn = (
    queryClient: QueryClient,
    wd: WorkspaceDescriptor,
    inn: string
) =>
    queryClient.fetchQuery<InnInfo>({
        queryKey: apiQueryKey.checkInn(wd, inn),
        queryFn: () =>
            api.get<InnInfo>(
                `/ord/check-inn/${wd.workspace_type}/${wd.workspace_id}/${inn}`
            ),
        staleTime: 1000 * 60,
    })

export function useGetVkOrdHistory(
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<UseQueryOptions<VkOrdHistoryResponse[], Error>, 'queryKey'>
) {
    return useQuery<VkOrdHistoryResponse[], Error>({
        queryKey: apiQueryKey.vkOrdHistory(wwld),
        queryFn: () => getVkOrdHistory(wwld),
        ...options,
    })
}

export function useGetLinkedLinksByContract(
    wd: WorkspaceDescriptor,
    documentId: number,
    type: 'draft' | 'custom',
    options?: Omit<UseQueryOptions<LinkedLinksResponse, Error>, 'queryKey'>
) {
    return useQuery({
        queryKey: apiQueryKey.linkedLinksByContract(wd, type, documentId),
        queryFn: () =>
            api.get<LinkedLinksResponse>(
                `/ord/linked-links-by-contract/${type}/${documentId}`
            ),
        gcTime: 0, // т.к. пользователь может изменить бюджет, то нет смысла кешировать этот запрос
        staleTime: 0,
        ...options,
    })
}
